import { Head, useForm } from "@inertiajs/react";
import { type FormEvent, useEffect, useState } from "react";
import { Box, Button, Callout, Card, Flex, SegmentedControl, Separator, Text, TextField } from "@radix-ui/themes";
import { AlertCircle, Eye, Globe, Image, Mail, Palette, PowerOff, Trash2 } from "lucide-react";
import ManagerPageHeader from "@/shared/manager/ManagerPageHeader";
import { buttonText } from "@/shared/brand/buttonText";
import type { BrandsEditFormData, BrandsEditProps } from "@/types/brands_edit";

// The bar colours BrandPalette::TOPBARS sets; dark is Driftables' own.
const TOPBAR = {
  dark: { background: "var(--topbar-background)", foreground: "var(--topbar-foreground)" },
  light: { background: "#ffffff", foreground: "#1c2024" },
};

const HEX = /^#[0-9a-f]{6}$/i;

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <Flex direction="column" gap="2">
      <Text as="label" size="3" weight="medium">
        {label}
      </Text>
      {hint && (
        <Text size="2" color="gray">
          {hint}
        </Text>
      )}
      {children}
    </Flex>
  );
}

// A blob URL for a chosen-but-unsaved file, released when it changes.
function useObjectUrl(file: File | null) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) return setUrl(null);
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}

// Upload, or stage the removal of, one of the brand's images. Staged like the
// rest of the form: nothing changes until Save.
function AttachmentField({
  label,
  saved,
  staged,
  removing,
  onChoose,
  onRemove,
}: {
  label: string;
  saved: boolean;
  staged: File | null;
  removing: boolean;
  onChoose: (file: File | null) => void;
  onRemove: (remove: boolean) => void;
}) {
  return (
    <Flex direction="column" gap="2">
      {saved && !staged && (
        <Flex gap="2" align="center">
          {removing ? (
            <>
              <Text size="2" color="red">
                The {label} will be removed when you save.
              </Text>
              <Button type="button" variant="ghost" size="1" color="gray" onClick={() => onRemove(false)}>
                Undo
              </Button>
            </>
          ) : (
            <Button type="button" variant="ghost" size="1" color="red" onClick={() => onRemove(true)}>
              <Trash2 size={12} />
              Remove {label}
            </Button>
          )}
        </Flex>
      )}
      <Flex align="center" gap="2">
        <Image size={16} aria-hidden="true" />
        <input
          type="file"
          accept="image/*"
          aria-label={label}
          // Choosing a new one cancels a staged removal.
          onChange={(e) => onChoose(e.target.files?.[0] ?? null)}
          style={{ fontSize: "var(--font-size-2)" }}
        />
      </Flex>
    </Flex>
  );
}

export default function EditBrand({ brand, preview_path, errors }: BrandsEditProps) {
  const { data, setData, patch, processing } = useForm<BrandsEditFormData>({
    brand: {
      name: brand.name,
      accent_color: brand.accent_color,
      website_url: brand.website_url,
      reply_to: brand.reply_to,
      topbar: brand.topbar,
      logo: null,
      remove_logo: false,
      icon: null,
      remove_icon: false,
    },
  });
  const setBrand = (fields: Partial<BrandsEditFormData["brand"]>) => setData("brand", { ...data.brand, ...fields });

  // A chosen file previews before it's saved.
  const stagedLogoUrl = useObjectUrl(data.brand.logo);
  const stagedIconUrl = useObjectUrl(data.brand.icon);

  const logoUrl = stagedLogoUrl ?? (data.brand.remove_logo ? null : brand.logo_url);
  const iconUrl = stagedIconUrl ?? (data.brand.remove_icon ? null : brand.icon_url);
  const accent = HEX.test(data.brand.accent_color) ? data.brand.accent_color : brand.accent_color;
  const button = buttonText(accent);
  const bar = TOPBAR[data.brand.topbar];
  const displayName = data.brand.name || brand.owner_name;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    patch(`/branding/${brand.id}`);
  };

  return (
    <>
      <Head title="Branding" />
      <Box className="w-full">
        <ManagerPageHeader title="Branding">
          {preview_path && (
            <Button asChild variant="soft" size="2">
              {/* A full page load: the brand's CSS is in the layout's head. */}
              <a href={preview_path}>
                <Eye size={14} />
                Preview the site
              </a>
            </Button>
          )}
        </ManagerPageHeader>
        <Box px={{ initial: "3", sm: "4" }} py="3" className="max-w-3xl">
          <Text as="p" size="3" color="gray" mb="4">
            How {brand.owner_name}'s branded site looks
            {brand.host ? (
              <>
                {" "}
                at <Text weight="bold">{brand.host}</Text>
              </>
            ) : (
              ". Its domain isn't set up yet"
            )}
            .
          </Text>

          {!brand.active && (
            <Callout.Root color="amber" mb="4">
              <Callout.Icon>
                <PowerOff size={16} />
              </Callout.Icon>
              <Callout.Text>
                Switched off: there's no branded site, and {brand.owner_name}'s managers can't see this
                page. Turn it on in Avo.
              </Callout.Text>
            </Callout.Root>
          )}

          {errors.length > 0 && (
            <Callout.Root color="red" role="alert" mb="4">
              <Callout.Icon>
                <AlertCircle size={16} />
              </Callout.Icon>
              <Callout.Text>
                {errors.map((error) => (
                  <span key={error} className="block">
                    {error}
                  </span>
                ))}
              </Callout.Text>
            </Callout.Root>
          )}

          {/* Roughly what the top of the branded site will look like. */}
          <Card mb="6">
            <Text size="1" color="gray" weight="bold" className="uppercase tracking-wider">
              Preview
            </Text>
            <div
              className="mt-3 flex h-14 items-center justify-between gap-3 rounded px-4"
              style={{ background: bar.background, color: bar.foreground }}
            >
              {logoUrl ? (
                <img src={logoUrl} alt="" className="h-8 w-auto max-w-[12rem] object-contain" />
              ) : (
                <span className="font-['Oswald'] text-lg font-bold">{displayName}</span>
              )}
              <span className="text-[15px] font-medium" style={{ color: accent }}>
                Sign In
              </span>
            </div>
            <Flex align="center" gap="3" mt="4" wrap="wrap">
              <span
                className="rounded px-4 py-2 text-sm font-semibold"
                style={{ background: accent, color: button.text }}
              >
                Apply now
              </span>
              <Text size="2" weight="medium" style={{ color: accent }}>
                A link
              </Text>
            </Flex>
          </Card>

          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="5">
              <Field label="Name *" hint="What the branded site calls itself.">
                <TextField.Root
                  size="3"
                  required
                  value={data.brand.name}
                  onChange={(e) => setBrand({ name: e.target.value })}
                />
              </Field>

              <Field label="Accent colour *" hint="Buttons, links and highlights.">
                <Flex gap="3" align="center">
                  <input
                    type="color"
                    aria-label="Pick accent colour"
                    value={accent}
                    onChange={(e) => setBrand({ accent_color: e.target.value })}
                    className="h-10 w-14 cursor-pointer rounded border border-gray6 bg-transparent"
                  />
                  <div className="flex-1">
                    <TextField.Root
                      size="3"
                      required
                      value={data.brand.accent_color}
                      onChange={(e) => setBrand({ accent_color: e.target.value })}
                      placeholder="#ff2e89"
                    >
                      <TextField.Slot>
                        <Palette size={16} />
                      </TextField.Slot>
                    </TextField.Root>
                  </div>
                </Flex>
                {button.ratio < 3 && (
                  <Text size="2" color="amber">
                    Button text on this colour is hard to read (contrast {button.ratio}:1). A darker or
                    lighter shade will read better.
                  </Text>
                )}
              </Field>

              <Field label="Top bar" hint="In light and dark mode alike. Light suits a logo that's dark.">
                <div>
                  <SegmentedControl.Root
                    value={data.brand.topbar}
                    onValueChange={(value) => setBrand({ topbar: value as "dark" | "light" })}
                  >
                    <SegmentedControl.Item value="dark">Dark</SegmentedControl.Item>
                    <SegmentedControl.Item value="light">Light</SegmentedControl.Item>
                  </SegmentedControl.Root>
                </div>
              </Field>

              <Field label="Logo" hint="Shown in the branded site's top bar. A wide image works best.">
                <AttachmentField
                  label="logo"
                  saved={Boolean(brand.logo_url)}
                  staged={data.brand.logo}
                  removing={data.brand.remove_logo}
                  onChoose={(file) => setBrand({ logo: file, remove_logo: false })}
                  onRemove={(remove) => setBrand({ remove_logo: remove })}
                />
              </Field>

              <Field
                label="Icon"
                hint="Square, for the browser tab and phone home screens. Without one, the tab shows Driftables' icon."
              >
                <Flex align="center" gap="3">
                  {iconUrl && (
                    <img src={iconUrl} alt="" className="h-12 w-12 rounded border border-gray6 object-cover" />
                  )}
                  <AttachmentField
                    label="icon"
                    saved={Boolean(brand.icon_url)}
                    staged={data.brand.icon}
                    removing={data.brand.remove_icon}
                    onChoose={(file) => setBrand({ icon: file, remove_icon: false })}
                    onRemove={(remove) => setBrand({ remove_icon: remove })}
                  />
                </Flex>
              </Field>

              <Separator size="4" />

              <Field label="Your website" hint={`Where "Back to ${displayName}" links go.`}>
                <TextField.Root
                  size="3"
                  type="url"
                  value={data.brand.website_url}
                  onChange={(e) => setBrand({ website_url: e.target.value })}
                  placeholder="https://example.com"
                >
                  <TextField.Slot>
                    <Globe size={16} />
                  </TextField.Slot>
                </TextField.Root>
              </Field>

              <Field label="Reply-to email" hint="When someone replies to an email from your site, it comes here.">
                <TextField.Root
                  size="3"
                  type="email"
                  value={data.brand.reply_to}
                  onChange={(e) => setBrand({ reply_to: e.target.value })}
                  placeholder="events@example.com"
                >
                  <TextField.Slot>
                    <Mail size={16} />
                  </TextField.Slot>
                </TextField.Root>
              </Field>

              <Flex justify="end">
                <Button size="3" type="submit" disabled={processing}>
                  Save branding
                </Button>
              </Flex>
            </Flex>
          </form>
        </Box>
      </Box>
    </>
  );
}
